/**
 * Market saturation (spec §9.1, plan §6 M2-07). Each station keeps a lazy saturation `s` per item
 * (`MarketState`, saved in `WorldDeltas.markets`): a sale adds `qty / capacity`, a purchase removes it (floor 0),
 * and `s` decays exponentially with a half-life in game seconds. Entries store `{ s, t }` and decay on read, so
 * nothing ticks. The price factor is `satMult(s) = 1 / (1 + k·s)`.
 *
 * Pure and immutable: `withSale` and `withPurchase` return a new `MarketState` sharing untouched stations.
 */
import type { StationId } from '../data/m2Schemas'
import type { MarketEntry, MarketState } from '../space/contracts'

/** Saturation of `entry` at game time `t` (0 for a missing entry; no decay backwards in time). */
export function saturationAt(entry: MarketEntry | undefined, t: number, halfLife: number): number {
  if (entry === undefined || entry.s <= 0) return 0
  const dt = t - entry.t
  if (dt <= 0) return entry.s
  return entry.s * Math.pow(0.5, dt / halfLife)
}

/** The saturation price factor `1 / (1 + k·s)`. */
export function satMult(s: number, k: number): number {
  return 1 / (1 + k * s)
}

/**
 * The mean of `satMult` while saturation moves linearly from `s0` to `s1` (either direction): the exact
 * integral `ln((1 + k·s1) / (1 + k·s0)) / (k·(s1 − s0))`, i.e. the limit of pricing a trade tonne by tonne.
 */
export function meanSatMult(s0: number, s1: number, k: number): number {
  const ds = s1 - s0
  if (Math.abs(ds) < 1e-12) return satMult(s0, k)
  return Math.log((1 + k * s1) / (1 + k * s0)) / (k * ds)
}

/** The stored entry of `key` at `station`, if any. */
export function marketEntry(market: MarketState, station: StationId, key: string): MarketEntry | undefined {
  const byKey = market[station] as Record<string, MarketEntry> | undefined
  return byKey === undefined ? undefined : byKey[key]
}

/** Current saturation of `key` at `station` (decayed to `t`). */
export function saturationOf(market: MarketState, station: StationId, key: string, t: number, halfLife: number): number {
  return saturationAt(marketEntry(market, station, key), t, halfLife)
}

/** `market` with the entry of `key` at `station` set to `{ s, t }` (a new object; other stations shared). */
export function withSaturation(market: MarketState, station: StationId, key: string, s: number, t: number): MarketState {
  const byKey = (market[station] as Record<string, MarketEntry> | undefined) ?? {}
  return { ...market, [station]: { ...byKey, [key]: { s: s > 0 ? s : 0, t } } }
}

/**
 * The market after the player sells `qty` saturation units (tonnes of a ware, a component's tonnes) of `key` at
 * `station`: `s` decays to `t`, then rises by `qty / capacity`.
 */
export function withSale(
  market: MarketState,
  station: StationId,
  key: string,
  qty: number,
  capacity: number,
  t: number,
  halfLife: number,
): MarketState {
  const s = saturationOf(market, station, key, t, halfLife) + qty / capacity
  return withSaturation(market, station, key, s, t)
}

/** The market after the player buys `qty` of `key` at `station`: `s` decays, then falls by `qty / capacity` (floor 0). */
export function withPurchase(
  market: MarketState,
  station: StationId,
  key: string,
  qty: number,
  capacity: number,
  t: number,
  halfLife: number,
): MarketState {
  const s = saturationOf(market, station, key, t, halfLife) - qty / capacity
  return withSaturation(market, station, key, s, t)
}
